import React, { useState } from 'react';
import { 
  ShieldAlert, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Check, 
  X, 
  UserCheck, 
  FileText, 
  ArrowRight, 
  Building2, 
  DollarSign, 
  LockKeyhole,
  Clock
} from 'lucide-react';
import { Incident } from '../types';

interface ReviewModalProps {
  incident: Incident | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (incidentId: string, note: string) => Promise<void>;
  onReject: (incidentId: string, note: string) => Promise<void>;
  onQuarantine?: (incidentId: string) => Promise<void>;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  incident,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onQuarantine
}) => {
  const [operatorNote, setOperatorNote] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !incident) return null;

  const handleAction = async (action: 'approve' | 'reject' | 'quarantine') => {
    if (!operatorNote.trim()) {
      setError('Please provide an operator decision justification for the audit trail.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (action === 'approve') {
        await onApprove(incident.id, operatorNote.trim());
      } else if (action === 'reject') {
        await onReject(incident.id, operatorNote.trim());
      } else if (action === 'quarantine' && onQuarantine) {
        await onQuarantine(incident.id);
      }
      setOperatorNote('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record decision.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in">
      <div className="glass-panel w-full max-w-2xl rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto">
        
        {/* Modal Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                  Dual-Control Gate
                </span>
                <span className="text-xs font-mono text-slate-400">ID: {incident.id}</span>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mt-0.5">
                Maker-Checker Human Review Queue
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Incident Summary Card */}
        <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-bold text-sm text-slate-900 dark:text-white">{incident.title}</div>
            <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{new Date(incident.created_at).toLocaleString()}</span>
            </span>
          </div>

          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{incident.summary}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 text-[11px] font-mono">
            <div className="p-2 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
              <span className="text-slate-400 block text-[9px]">AGENT ID</span>
              <span className="font-bold text-slate-800 dark:text-slate-200">{incident.agent_id}</span>
            </div>
            <div className="p-2 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
              <span className="text-slate-400 block text-[9px]">ACTION TOOL</span>
              <span className="font-bold text-slate-800 dark:text-slate-200">{incident.action_name || 'execute_tool'}()</span>
            </div>
            <div className="p-2 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 col-span-2 sm:col-span-1">
              <span className="text-slate-400 block text-[9px]">SEVERITY</span>
              <span className="font-bold text-amber-600 dark:text-amber-400 uppercase">{incident.severity}</span>
            </div>
          </div>
        </div>

        {/* Recommended Runbook Steps */}
        {incident.runbook_steps && incident.runbook_steps.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Security Runbook Verification:</h4>
            <div className="p-3 rounded-xl bg-brand-50/50 dark:bg-brand-950/20 border border-brand-200/60 dark:border-brand-900/40 text-xs text-brand-900 dark:text-brand-200 space-y-1">
              {incident.runbook_steps.map((step, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="font-bold text-brand-600 dark:text-brand-400">{idx + 1}.</span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Operator Justification Form */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
            <span>Operator Decision Justification (Required for Audit Trail)</span>
            <span className="text-[10px] text-slate-400">Mapped to NIST AI RMF & SOC 2 CC-series</span>
          </label>
          <textarea
            rows={3}
            value={operatorNote}
            onChange={(e) => setOperatorNote(e.target.value)}
            placeholder="e.g. Verified vendor invoice PO-9912 directly with procurement lead. Approved for payment release."
            className="w-full rounded-2xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 p-3 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-brand-500 transition"
          />
          {error && (
            <p className="text-xs font-bold text-rose-600 dark:text-rose-400">{error}</p>
          )}
        </div>

        {/* Modal Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
          {onQuarantine && (
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleAction('quarantine')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 transition self-start sm:self-auto"
            >
              <LockKeyhole className="w-3.5 h-3.5" />
              <span>Quarantine Agent</span>
            </button>
          )}

          <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end ml-auto">
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleAction('reject')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 disabled:opacity-50 shadow-sm transition"
            >
              <XCircle className="w-4 h-4" />
              <span>Reject & Lock</span>
            </button>

            <button
              type="button"
              disabled={submitting}
              onClick={() => handleAction('approve')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 shadow-sm transition"
            >
              <CheckCircle className="w-4 h-4" />
              <span>Approve & Release</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
